import startOfDay from 'date-fns/startOfDay'
import subMonths from 'date-fns/subMonths'
import { Formik } from 'formik'
import { keys, isEmpty, uniqBy } from 'lodash'
import { useState, ReactElement } from 'react'
import { FormattedMessage, defineMessages, useIntl } from 'react-intl'
import { useNavigate } from 'react-router-dom'

import {
  FilterHeader,
  FilterWrapper,
  TagsWrapper,
  FiltersWrapper,
  FilterToggle,
  FiltersContainer,
  FiltersSubmit,
  ApplyButtonWrapper,
  ResetButton,
  FilterButtonsWrapper
} from 'components/Filters'
import { Input } from 'components/Input'
import { InputTags } from 'components/v2/InputTags'
import { Label } from 'components/v2/Label'
import { SelectTags } from 'components/v2/SelectTags'
import { Tag } from 'components/v2/Tag'
import { DateRangeFilter } from 'features/App/components/Filters/DateRangeFilter'
import { IDateRangeFilter } from 'features/App/components/Filters/DateRangeFilter/types'
import { statusMessages } from 'features/Bookings/components/Status/messages'
import { PlaceAutocomplete } from 'features/Places/components/PlaceAutocomplete'
import { SiteAutocomplete } from 'features/Sites/components/SiteAutocomplete'
import { dateRangeToString, getDateRangeQuery } from 'utils/date'
import { getQueryString } from 'utils/route'
import { IBookingFiltersProps, IBookingFilters, IBookingFilterSite, IBookingFilterPlace } from './types'
import { ISalesChannel } from 'features/Services/components/NewServicesV2/components/OfferSettingsCard/types'
import { SelectSalesChannelV2 } from 'features/Services/components/SelectSalesChannelV2'
import { SelectEmailStatus, emailStatusMessages } from 'components/SelectEmailStatus'
import { EEmailStatus } from 'models/bookings/types'
import { safeFormatMessage } from 'utils/intl'
import { Button } from 'components/v2/Button'
import { getDifference } from 'utils/object'
import { CompanyLineAutocomplete } from 'features/Lines/components/CompanyLineAutocomplete'
import { GTBookingV2Status, GTRouteServiceSuggestion } from 'gql/types.generated'
import { MultiRouteServiceAutocomplete } from 'features/Services/components/MultiRouteServiceAutocomplete'
import { getShortId } from 'utils/string'
import { subDays } from 'date-fns'

const STATUS_LIST = [
  GTBookingV2Status.Confirmed,
  GTBookingV2Status.PartiallyConfirmed,
  GTBookingV2Status.Reserved,
  GTBookingV2Status.Incomplete,
  GTBookingV2Status.Failed,
  GTBookingV2Status.Paid,
  GTBookingV2Status.Cancelled
]

const messages = defineMessages({
  selectSaleChannel: {
    id: 'bookings.filters.selectSaleChannel',
    defaultMessage: 'Select sale channel'
  },
  selectStatus: {
    id: 'bookings.filters.selectStatus',
    defaultMessage: 'Select status'
  },
  selectEmailStatus: {
    id: 'bookings.filters.selectEmailStatus',
    defaultMessage: 'Select email status'
  },
  selectTravelDate: {
    id: 'bookings.filters.selectTravelDate',
    defaultMessage: 'Select travel date'
  },
  selectTravelDateRange: {
    id: 'bookings.filters.selectTravelDateRange',
    defaultMessage: 'Select travel date range'
  },
  emailPlaceholder: {
    id: 'bookings.filters.emailPlaceholder',
    defaultMessage: 'Client email'
  },
  reservationsPlaceholder: {
    id: 'bookings.filters.reservationsPlaceholder',
    defaultMessage: 'Reservations No.'
  },
  selectPurchaseDate: {
    id: 'bookings.filters.selectPurchaseDate',
    defaultMessage: 'Select purchase date'
  },
  selectPurchaseDateRange: {
    id: 'bookings.filters.selectPurchaseDateRange',
    defaultMessage: 'Select purchase date range'
  },
  selectSortingDateRange: {
    id: 'bookings.filters.selectSortingDateRange',
    defaultMessage: 'Select sorting date range'
  },
  selectOrigin: {
    id: 'bookings.filters.selectOrigin',
    defaultMessage: 'Select origin'
  },
  selectDestination: {
    id: 'bookings.filters.selectDestination',
    defaultMessage: 'Select destination'
  },
  selectSite: {
    id: 'bookings.filters.selectSite',
    defaultMessage: 'Select PoS'
  },
  serviceCodesPlaceholder: {
    id: 'bookings.filters.serviceCodes.placeholder',
    defaultMessage: 'E.g: CR041022'
  },
  buyerPhonePlaceholder: {
    id: 'bookings.filters.buyerPhonePlaceholder',
    defaultMessage: 'Client phone'
  },
  selectCompanyLine: {
    id: 'bookings.filters.selectCompanyLine',
    defaultMessage: 'Select company line'
  },
  selectRouteServices: {
    id: 'bookings.filters.selectRouteServices',
    defaultMessage: 'Select route services'
  },
  couponPlaceholder: {
    id: 'bookings.filters.couponPlaceholder',
    defaultMessage: 'Coupon code'
  }
})

const getFilteredStatus = (values: string[]): string[] => {
  return STATUS_LIST.filter((status): TSFixMe => {
    return values.indexOf(status) === -1
  })
}

export const BookingFilters = ({ filters }: IBookingFiltersProps): ReactElement => {
  const navigate = useNavigate()
  const intl = useIntl()
  const [isShowingFilters, showFilters] = useState(false)

  const toggleFilters = (): void => {
    showFilters(!isShowingFilters)
  }

  const filterBookings = (values: IBookingFilters): void => {
    const travelDateRangeQuery = getDateRangeQuery(values.travelDateRange)
    const purchaseDateRangeQuery = getDateRangeQuery(values.purchaseDateRange)
    const sortingDateRangeQuery = getDateRangeQuery(values.sortingDateRange)
    const query = {
      salesChannelId: values.salesChannel?.id,
      email: values.email,
      travelDateFrom: travelDateRangeQuery.from,
      travelDateTo: travelDateRangeQuery.to,
      travelDateRangeValue: travelDateRangeQuery.rangeValue,
      purchaseDateFrom: purchaseDateRangeQuery.from,
      purchaseDateTo: purchaseDateRangeQuery.to,
      purchaseDateRangeValue: purchaseDateRangeQuery.rangeValue,
      sortingDateFrom: sortingDateRangeQuery.from,
      sortingDateTo: sortingDateRangeQuery.to,
      sortingDateRangeValue: sortingDateRangeQuery.rangeValue,
      origin: values.origin.id,
      destination: values.destination.id,
      status: values.status.join(','),
      emailStatus: values.emailStatus,
      site: values.site.id,
      reservations: values.reservations.map((reservation) => getShortId(reservation)).join(','),
      buyerPhone: values.buyerPhone,
      companyLineId: values.companyLine?.id,
      routeServiceIds: values.routeServices?.map((service): string => service.id).join(','),
      coupon: values.coupon
    }

    navigate(`/bookings?${getQueryString(query)}`)
  }

  const now = startOfDay(new Date())
  const defaultValues: IBookingFilters = {
    salesChannel: { id: '', name: '' },
    status: [],
    emailStatus: '' as EEmailStatus | '',
    email: '',
    reservations: [],
    origin: { id: '', name: '' },
    destination: { id: '', name: '' },
    site: { id: '', name: '' },
    purchaseDateRange: {
      from: '',
      to: '',
      rangeValue: ''
    },
    travelDateRange: {
      from: '',
      to: '',
      rangeValue: ''
    },
    sortingDateRange: {
      from: subMonths(now, 4),
      to: now,
      rangeValue: ''
    },
    buyerPhone: '',
    companyLine: { id: '', name: '' },
    routeServices: [],
    coupon: ''
  }

  const initialValues = {
    salesChannel: filters.salesChannel || defaultValues.salesChannel,
    status: filters.status || defaultValues.status,
    emailStatus: filters.emailStatus || defaultValues.emailStatus,
    email: filters.email || defaultValues.email,
    reservations: filters.reservations || defaultValues.reservations,
    origin: filters.origin || defaultValues.origin,
    destination: filters.destination || defaultValues.destination,
    site: filters.site || defaultValues.site,
    purchaseDateRange: filters.purchaseDateRange || defaultValues.purchaseDateRange,
    travelDateRange: filters.travelDateRange || defaultValues.travelDateRange,
    buyerPhone: filters.buyerPhone || defaultValues.buyerPhone,
    sortingDateRange: filters.sortingDateRange || defaultValues.sortingDateRange,
    companyLine: filters.companyLine || defaultValues.companyLine,
    routeServices: filters.routeServices || defaultValues.routeServices,
    coupon: filters.coupon || defaultValues.coupon
  }

  const handleSubmit = (): void => {
    // Do nothing, only apply filters when aply button is pressed
  }

  return (
    <Formik initialValues={initialValues} onSubmit={handleSubmit} enableReinitialize>
      {(props): ReactElement => {
        const { handleChange, handleSubmit, setFieldValue, setValues, values } = props

        const handleStatusChange = (status: string[]): void => {
          setFieldValue('status', status)
        }

        const handleReservationsChange = (reservations: string[]): void => {
          setFieldValue(
            'reservations',
            reservations.map((reservation) => getShortId(reservation))
          )
        }

        const handleFilterDelete = (id: keyof typeof defaultValues): void => {
          setFieldValue(id, defaultValues[id])
        }

        const handleSalesChannelChange = (salesChannel: ISalesChannel): void => {
          setFieldValue('salesChannel', salesChannel)
        }

        const handleResetFilters = (): void => {
          setValues(defaultValues)
          filterBookings(defaultValues)
        }

        const hasPendingFilters = !isEmpty(getDifference(initialValues, values))

        return (
          <form onSubmit={handleSubmit}>
            <div>
              <FilterHeader>
                <FilterWrapper>
                  <FilterButtonsWrapper>
                    <FilterToggle onClick={toggleFilters} />
                    <ResetButton onClick={handleResetFilters} />
                  </FilterButtonsWrapper>
                  <TagsWrapper>
                    {keys(filters).map((filterKey): TSFixMe => {
                      let label, value
                      const filterValue = values[filterKey as keyof typeof values]
                      const hasValue = !isEmpty(filterValue)

                      if (!hasValue) return null

                      switch (filterKey) {
                        case 'salesChannel':
                          label = <FormattedMessage id="dictionary.salesChannel" defaultMessage="Sales channel" />

                          if (!(filterValue as ISalesChannel)?.id) return null

                          value = (filterValue as ISalesChannel).name
                          break
                        case 'status':
                          label = <FormattedMessage id="dictionary.status" defaultMessage="Status" />
                          value = (filterValue as string[])
                            .map((status): string => {
                              return safeFormatMessage({ intl, messages: statusMessages, key: status })
                            })
                            .join(', ')
                          break
                        case 'emailStatus':
                          label = <FormattedMessage id="dictionary.emailStatus" defaultMessage="Email status" />
                          value = safeFormatMessage({
                            intl,
                            key: filterValue as EEmailStatus | '',
                            messages: emailStatusMessages
                          })

                          break
                        case 'travelDateRange':
                          label = <FormattedMessage id="dictionary.travelDate" defaultMessage="Travel date" />
                          value = dateRangeToString(intl, filterValue as IDateRangeFilter)

                          if (!value) return null

                          break
                        case 'email':
                          label = <FormattedMessage id="dictionary.email" defaultMessage="Email" />
                          value = filterValue as string
                          break
                        case 'reservations':
                          label = <FormattedMessage id="dictionary.reservations" defaultMessage="Reservations" />
                          value = (filterValue as string[]).join(', ')
                          break
                        case 'purchaseDateRange':
                          label = <FormattedMessage id="dictionary.purchaseDate" defaultMessage="Purchase date" />
                          value = dateRangeToString(intl, filterValue as IDateRangeFilter)

                          if (!value) return null

                          break
                        case 'sortingDateRange':
                          label = <FormattedMessage id="dictionary.sortingDate" defaultMessage="Sorting date" />
                          value = dateRangeToString(intl, filterValue as IDateRangeFilter)

                          if (!value) return null

                          break
                        case 'origin':
                          label = <FormattedMessage id="dictionary.origin" defaultMessage="Origin" />

                          if (!(filterValue as IBookingFilterPlace)?.id) return null

                          value = (filterValue as IBookingFilterPlace).name
                          break
                        case 'destination':
                          label = <FormattedMessage id="dictionary.destination" defaultMessage="Destination" />

                          if (!(filterValue as IBookingFilterPlace)?.id) return null

                          value = (filterValue as IBookingFilterPlace).name
                          break
                        case 'site':
                          label = <FormattedMessage id="dictionary.pos" defaultMessage="PoS" />

                          if (!(filterValue as IBookingFilterSite)?.id) return null

                          value = (filterValue as IBookingFilterSite).name
                          break
                        case 'buyerPhone':
                          label = <FormattedMessage id="dictionary.phone" defaultMessage="Phone" />
                          value = filterValue as string
                          break
                        case 'companyLine':
                          label = <FormattedMessage id="dictionary.companyLine" defaultMessage="Company line" />

                          if (!(filterValue as IBookingFilters['companyLine'])?.id) return null

                          value = (filterValue as IBookingFilters['companyLine']).name
                          break
                        case 'routeServices':
                          label = <FormattedMessage id="dictionary.routeServices" defaultMessage="Route services" />
                          value = uniqBy(filterValue as IBookingFilters['routeServices'], 'code')
                            .map((service): string => service.code)
                            .join(', ')
                          break
                        case 'coupon':
                          label = <FormattedMessage id="dictionary.coupon" defaultMessage="Coupon" />
                          value = filterValue as string
                          break
                      }

                      return (
                        <Tag
                          key={filterKey}
                          onDelete={handleFilterDelete.bind(this, filterKey as keyof typeof defaultValues)}
                        >
                          {label}: {value}
                        </Tag>
                      )
                    })}
                  </TagsWrapper>
                  <ApplyButtonWrapper>
                    <Button
                      kind="primary"
                      type="button"
                      disabled={!hasPendingFilters}
                      onClick={(): void => filterBookings(values)}
                    >
                      <FormattedMessage id="dictionary.apply" defaultMessage="Apply" />
                    </Button>
                  </ApplyButtonWrapper>
                </FilterWrapper>
              </FilterHeader>
              <FiltersWrapper isShowingFilters={isShowingFilters}>
                <FiltersContainer>
                  <div>
                    <Label>
                      <FormattedMessage id="dictionary.SortingDateRange" defaultMessage="Sorting date range" />
                    </Label>
                    <DateRangeFilter
                      value={values.sortingDateRange}
                      placeholder={intl.formatMessage(messages.selectSortingDateRange)}
                      onChange={(value: IDateRangeFilter): void => {
                        setFieldValue('sortingDateRange', value)
                      }}
                    />
                  </div>
                  <SelectSalesChannelV2
                    value={values.salesChannel}
                    onChange={handleSalesChannelChange}
                    label={<FormattedMessage id="dictionary.salesChannel" defaultMessage="Sales channel" />}
                  />
                  <CompanyLineAutocomplete
                    id="companyLine"
                    placeholder={intl.formatMessage(messages.selectCompanyLine)}
                    value={values.companyLine.name}
                    label={<FormattedMessage id="dictionary.companyLine" defaultMessage="Company line" />}
                    onChange={(companyLine: IBookingFilterSite): void => {
                      setFieldValue('companyLine', companyLine)
                    }}
                  />
                  <SelectTags
                    id="status"
                    placeholder={intl.formatMessage(messages.selectStatus)}
                    values={values.status}
                    items={getFilteredStatus(values.status)}
                    getTagLabel={(status: GTBookingV2Status): string => {
                      return safeFormatMessage({ intl, messages: statusMessages, key: status })
                    }}
                    onChange={handleStatusChange}
                    label={<FormattedMessage id="dictionary.status" defaultMessage="Status" />}
                  />
                  <SelectEmailStatus
                    id="emailStatus"
                    value={values.emailStatus as EEmailStatus | ''}
                    onChange={(emailStatus: EEmailStatus | ''): void => {
                      setFieldValue('emailStatus', emailStatus)
                    }}
                    label={<FormattedMessage id="dictionary.emailStatus" defaultMessage="Email status" />}
                    placeholder={intl.formatMessage(messages.selectEmailStatus)}
                  />
                  <Input
                    id="email"
                    value={values.email}
                    onChange={handleChange}
                    placeholder={intl.formatMessage(messages.emailPlaceholder)}
                    label={<FormattedMessage id="dictionary.email" defaultMessage="Email" />}
                  />
                  <InputTags
                    id="reservations"
                    values={values.reservations}
                    onChange={handleReservationsChange}
                    placeholder={intl.formatMessage(messages.reservationsPlaceholder)}
                    label={<FormattedMessage id="dictionary.reservations" defaultMessage="Reservations" />}
                  />
                  <MultiRouteServiceAutocomplete
                    id="routeServices"
                    placeholder={intl.formatMessage(messages.selectRouteServices)}
                    values={values.routeServices}
                    label={<FormattedMessage id="dictionary.routeServices" defaultMessage="Route services" />}
                    onChange={(routeServices: GTRouteServiceSuggestion[]): void => {
                      setFieldValue('routeServices', routeServices)
                    }}
                    uniqueCode
                    endAt={{ start: subDays(startOfDay(new Date()), 30) }}
                  />
                  <div>
                    <Label>
                      <FormattedMessage id="dictionary.purchaseDateRange" defaultMessage="Purchase date range" />
                    </Label>
                    <DateRangeFilter
                      value={values.purchaseDateRange}
                      placeholder={intl.formatMessage(messages.selectPurchaseDateRange)}
                      onChange={(value: IDateRangeFilter): void => {
                        setFieldValue('purchaseDateRange', value)
                      }}
                    />
                  </div>
                  <div>
                    <Label>
                      <FormattedMessage id="dictionary.travelDateRange" defaultMessage="Travel date range" />
                    </Label>
                    <DateRangeFilter
                      value={values.travelDateRange}
                      placeholder={intl.formatMessage(messages.selectTravelDateRange)}
                      onChange={(value: IDateRangeFilter): void => {
                        setFieldValue('travelDateRange', value)
                      }}
                    />
                  </div>
                  <PlaceAutocomplete
                    placeholder={intl.formatMessage(messages.selectOrigin)}
                    value={values.origin.name}
                    label={<FormattedMessage id="dictionary.origin" defaultMessage="Origin" />}
                    onChange={(place: IBookingFilterPlace): void => {
                      setFieldValue('origin', place)
                    }}
                  />
                  <PlaceAutocomplete
                    placeholder={intl.formatMessage(messages.selectDestination)}
                    value={values.destination.name}
                    label={<FormattedMessage id="dictionary.destination" defaultMessage="Destination" />}
                    onChange={(place: IBookingFilterPlace): void => {
                      setFieldValue('destination', place)
                    }}
                  />
                  <SiteAutocomplete
                    placeholder={intl.formatMessage(messages.selectSite)}
                    value={values.site.name}
                    label={<FormattedMessage id="dictionary.pos" defaultMessage="PoS" />}
                    onChange={(site: IBookingFilterSite): void => {
                      setFieldValue('site', site)
                    }}
                  />
                  <Input
                    id="buyerPhone"
                    value={values.buyerPhone}
                    onChange={handleChange}
                    placeholder={intl.formatMessage(messages.buyerPhonePlaceholder)}
                    label={<FormattedMessage id="dictionary.phone" defaultMessage="Phone" />}
                  />
                  <Input
                    id="coupon"
                    value={values.coupon}
                    onChange={handleChange}
                    placeholder={intl.formatMessage(messages.couponPlaceholder)}
                    label={<FormattedMessage id="dictionary.coupon" defaultMessage="Coupon" />}
                  />
                </FiltersContainer>
              </FiltersWrapper>
              <FiltersSubmit />
            </div>
          </form>
        )
      }}
    </Formik>
  )
}
